/**
 * A demo page's isolation from the real prest0.ai (exception M1) — run by src/demo/demoBoot.ts before any other module
 * of the app evaluates, and only on a demo page. ON THE FIRST DOWNLOAD: kept to what must happen before the app's own
 * modules run; everything else waits in the demo's runtime chunk (src/demo/runtime/).
 *
 *  - Nothing of the real site is read but two facts the band needs (a real session exists; this device signed in
 *    before), and nothing of it is ever written: local and session storage become in-memory stores (mirrored into ONE
 *    real sessionStorage key, so a reload or hard navigation keeps the demo's own state), IndexedDB and
 *    BroadcastChannel names carry the demo's prefix, and the real site's `storage` events never reach the app.
 *  - Nothing leaves the page: a Content-Security-Policy backstop lets the browser itself refuse any other origin, and
 *    every request the app makes that is not a static file of this site waits for the demo's scripted server (the
 *    runtime's router) — it is never sent.
 *  - The visitor is signed in as the demo's Owner: the Auth SDK's stored user is seeded where the SDK reads it, under
 *    the demo project's own key (src/demo/demoFirebaseConfig.ts); the router answers the SDK's two calls.
 *  - Fail closed: each of these is put in place on its own, then checked. If any did not take (a browser, or an
 *    extension, holding a property fast), the page is marked broken (`broken` names which), the router is refused for
 *    good — nothing held is ever answered or sent — the demo's failure line is drawn, and the boot throws, so the app's
 *    own modules never evaluate.
 */
import { buildEnv } from '../../config/buildEnv';
import type { DemoIndustryId } from '../demoIndustries';
import { demoFirebaseConfig, demoOwnerUid } from '../demoFirebaseConfig';

/** A request the router answers: what the page asked for, as `fetch` would take it. */
export type DemoRoute = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/** What a demo page says when the demo cannot run: the boot draws it, and the door (src/demo/DemoDoor.tsx) shows it. */
// copy: demo_load_failed
export const DEMO_LOAD_FAILED_LINE = "The demo couldn't load. Reload the page to try again.";

export interface DemoBootState {
  industry: DemoIndustryId;
  /** A real Firebase session exists in this browser (the band then offers the person's own workspace). */
  realSession: boolean;
  /** This device signed in to prest0.ai before (`prest0_has_signed_in`, src/services/guestChat/client.ts). */
  signedInBefore: boolean;
  /** The browser's own functions, captured before anything wrapped them. */
  natives: { fetch: typeof fetch; XMLHttpRequest: typeof XMLHttpRequest; sessionStorage: Storage };
  /** The demo's in-memory local and session storage. */
  stores: { local: Storage; session: Storage };
  /** What did not take when the page was isolated (empty when everything did); a broken page never releases. */
  broken: string[];
  /** The runtime hands its router over here; every request held until then is answered by it. */
  release(route: DemoRoute): void;
  /** True once the router is in place. */
  routed(): boolean;
  /** What `window.Stripe` makes on a demo page: the runtime's stand-in (src/demo/runtime/stripeStandIn.ts). */
  stripe: ((...args: unknown[]) => unknown) | null;
  /** Start over: the demo's storage is emptied and no longer kept for the day (the page reloads next). */
  forget(): void;
}

let state: DemoBootState | null = null;

/** The boot's state on a demo page (null elsewhere). */
export function demoBootState(): DemoBootState | null {
  return state;
}

// `/demo/a/f/…` is a file the demo builds in the browser and `/demo/a/u/…` one the visitor uploaded, both answered by
// the router; the rest of `/demo/a/` is static, as are `/demo-media/` (Prest0n's recorded read-aloud) and `/demo-desktop/`.
const STATIC_PATH = /^\/(?:static|demo\/a\/(?![fu]\/)|demo-media\/|demo-desktop|fonts|images|icons?|favicon[^/]*|logo[^/]*|manifest\.json$|asset-manifest\.json$|environment\.js$|staticProps\.js$)/i;

/** A static file of this site (or an inline URL): the only requests a demo page sends as they are. */
export function isStaticRequest(url: string, method = 'GET'): boolean {
  if (/^(?:data|blob):/i.test(url)) return true;
  let parsed: URL;
  try {
    parsed = new URL(url, window.location.href);
  } catch {
    return false;
  }
  return parsed.origin === window.location.origin && /^(?:GET|HEAD)$/i.test(method) && STATIC_PATH.test(parsed.pathname);
}

const dayKey = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
};

/** An in-memory Storage that also answers property reads, `Object.keys` and `in`, as the browser's does. */
function memoryStorage(initial: Record<string, string>, changed: () => void): Storage {
  const map = new Map<string, string>(Object.entries(initial));
  const api = {
    get length() {
      return map.size;
    },
    key: (i: number) => Array.from(map.keys())[i] ?? null,
    getItem: (k: string) => (map.has(String(k)) ? (map.get(String(k)) as string) : null),
    setItem: (k: string, v: string) => {
      map.set(String(k), String(v));
      changed();
    },
    removeItem: (k: string) => {
      if (map.delete(String(k))) changed();
    },
    clear: () => {
      map.clear();
      changed();
    },
  };
  return new Proxy(api, {
    get: (t, p) => (p in t ? (t as Record<string | symbol, unknown>)[p] : typeof p === 'string' ? t.getItem(p) ?? undefined : undefined),
    set: (t, p, v) => {
      if (typeof p === 'string') t.setItem(p, v);
      return true;
    },
    has: (t, p) => p in t || (typeof p === 'string' && map.has(p)),
    deleteProperty: (t, p) => {
      if (typeof p === 'string') t.removeItem(p);
      return true;
    },
    ownKeys: () => Array.from(map.keys()),
    getOwnPropertyDescriptor: (_t, p) =>
      typeof p === 'string' && map.has(p) ? { value: map.get(p), enumerable: true, configurable: true, writable: true } : undefined,
  }) as unknown as Storage;
}

const base64url = (value: unknown): string =>
  btoa(unescape(encodeURIComponent(JSON.stringify(value)))).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');

/** A token the Auth SDK accepts: three parts, the claims it reads, an hour of life (a longer one overflows its timer). */
export function demoIdToken(industry: DemoIndustryId, nowMs: number = Date.now(), email?: string): string {
  const now = Math.floor(nowMs / 1000);
  const uid = demoOwnerUid(industry);
  const project = demoFirebaseConfig(industry).projectId;
  const claims = {
    // names-only: the token's issuer claim, the shape the Auth SDK expects; never an address anything requests.
    iss: `https://securetoken.google.com/${project}`,
    aud: project,
    auth_time: now,
    iat: now,
    exp: now + 3600,
    user_id: uid,
    sub: uid,
    email: email || `${uid}@demo.example`,
    email_verified: true,
    firebase: { identities: {}, sign_in_provider: 'password' },
  };
  return `${base64url({ alg: 'none', typ: 'JWT' })}.${base64url(claims)}.ZGVtbw`;
}

/** The demo's failure line where the app would have drawn (the app never starts on a broken page). */
function drawFailure(): void {
  const draw = () => {
    const box = document.createElement('div');
    box.setAttribute('role', 'alert');
    box.style.cssText = 'max-width:420px;margin:20vh auto 0;padding:0 16px;font:500 16px/1.5 system-ui,sans-serif;text-align:center';
    box.textContent = DEMO_LOAD_FAILED_LINE;
    const root = document.getElementById('root') || document.body;
    root.textContent = '';
    root.appendChild(box);
  };
  try {
    if (document.body) draw();
    else document.addEventListener('DOMContentLoaded', draw, { once: true });
  } catch {
    /* the thrown error in the console is what is left */
  }
}

export function isolateDemoPage(industry: DemoIndustryId): DemoBootState {
  if (state) return state;
  const w = window;
  // Each override is put in place on its own; the self-check below names the ones that did not take.
  const put = (step: () => void) => {
    try {
      step();
    } catch {
      /* named by the self-check */
    }
  };
  let realLocal: Storage | null = null;
  let realSession: Storage | null = null;
  try {
    realLocal = w.localStorage;
    realSession = w.sessionStorage;
  } catch {
    /* storage refused: the demo keeps its state in memory only */
  }
  const RealXhr = w.XMLHttpRequest;
  const natives = { fetch: w.fetch.bind(w), XMLHttpRequest: RealXhr, sessionStorage: realSession || memoryStorage({}, () => undefined) };

  // The two facts the band needs, and nothing else of the real site.
  let hasRealSession = false;
  let signedInBefore = false;
  try {
    hasRealSession = !!realLocal && !!realLocal.getItem(`firebase:authUser:${buildEnv().REACT_APP_FIREBASE_API_KEY}:[DEFAULT]`);
    signedInBefore = !!realLocal && realLocal.getItem('prest0_has_signed_in') === '1';
  } catch {
    /* storage unreadable: the band offers "start free" */
  }

  // The browser refuses every other origin, whatever the app's code does.
  const csp = document.createElement('meta');
  csp.httpEquiv = 'Content-Security-Policy';
  csp.content = [
    "default-src 'self'",
    "script-src 'self' 'unsafe-inline'",
    "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com https://api.fontshare.com",
    "font-src 'self' data: https://fonts.gstatic.com https://cdn.fontshare.com",
    "img-src 'self' data: blob: https://logos.composio.dev",
    "media-src 'self' data: blob:",
    "connect-src 'self' data: blob:",
    "frame-src 'self' blob:",
    "worker-src 'self' blob:",
  ].join('; ');
  put(() => document.head.prepend(csp));

  // Storage: in memory, mirrored into one real sessionStorage key so a reload keeps the demo's own state for the day.
  const mirrorKey = `prest0demo:${industry}:mirror`;
  let saved: { d?: string; l?: Record<string, string>; s?: Record<string, string> } = {};
  try {
    saved = JSON.parse(natives.sessionStorage.getItem(mirrorKey) || '{}');
  } catch {
    saved = {};
  }
  if (saved.d !== dayKey()) saved = {};
  let pending: ReturnType<typeof setTimeout> | null = null;
  let keep = true;
  const save = () => {
    if (pending) clearTimeout(pending);
    pending = null;
    if (!keep) return;
    const pack = (s: Storage) => {
      const out: Record<string, string> = {};
      let total = 0;
      for (let i = 0; i < s.length; i += 1) {
        const k = s.key(i) as string;
        const v = s.getItem(k) as string;
        // One value up to 256 KB (the app's thread list for a busy firm is ~20 KB: without it a reload cannot find the
        // visitor's own thread), the whole mirror up to 1 MB.
        if (v.length > 262_144 || total + v.length > 1_000_000) continue;
        total += v.length;
        out[k] = v;
      }
      return out;
    };
    try {
      natives.sessionStorage.setItem(mirrorKey, JSON.stringify({ d: dayKey(), l: pack(stores.local), s: pack(stores.session) }));
    } catch {
      /* the mirror is a convenience; the demo runs without it */
    }
  };
  const changed = () => {
    if (!pending) pending = setTimeout(save, 250);
  };
  const stores = { local: memoryStorage(saved.l || {}, changed), session: memoryStorage(saved.s || {}, changed) };
  put(() => Object.defineProperty(w, 'localStorage', { configurable: true, enumerable: true, get: () => stores.local }));
  put(() => Object.defineProperty(w, 'sessionStorage', { configurable: true, enumerable: true, get: () => stores.session }));
  put(() => w.addEventListener('pagehide', save));
  // Another tab's change to the real site's storage is not the demo's business.
  put(() => w.addEventListener('storage', (e) => e.stopImmediatePropagation(), true));

  // IndexedDB and BroadcastChannel names carry the demo's prefix.
  const prefix = `prest0demo:${industry}:`;
  const idb = w.indexedDB;
  const idbOwn: Record<string, unknown> = {};
  if (idb) {
    const proto = Object.getPrototypeOf(idb);
    idbOwn.open = (name: string, version?: number) => proto.open.call(idb, prefix + name, version);
    idbOwn.deleteDatabase = (name: string) => proto.deleteDatabase.call(idb, prefix + name);
    if (typeof proto.databases === 'function') {
      idbOwn.databases = () =>
        proto.databases.call(idb).then((list: Array<{ name?: string; version?: number }>) =>
          list.filter((d) => (d.name || '').startsWith(prefix)).map((d) => ({ ...d, name: (d.name || '').slice(prefix.length) })),
        );
    }
    for (const [name, value] of Object.entries(idbOwn)) put(() => Object.defineProperty(idb, name, { configurable: true, value }));
  }
  const RealChannel = w.BroadcastChannel;
  const tab = Math.random().toString(36).slice(2);
  if (RealChannel) {
    put(() => {
      w.BroadcastChannel = class extends RealChannel {
        constructor(name: string) {
          super(`${prefix}${tab}:${name}`);
        }
      } as typeof BroadcastChannel;
    });
  }

  // Requests: static files go as they are; everything else waits for the router, and is never sent.
  let route: DemoRoute | null = null;
  const held: Array<() => void> = [];
  const demoFetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const method = (init && init.method) || (typeof input === 'object' && 'method' in input ? input.method : 'GET');
    if (isStaticRequest(url, method)) return natives.fetch(input as RequestInfo, init);
    if (route) return route(input, init);
    return new Promise<Response>((resolve, reject) => {
      held.push(() => (route as DemoRoute)(input, init).then(resolve, reject));
    });
  };
  put(() => {
    w.fetch = demoFetch as typeof fetch;
  });
  // Until the runtime's stand-in replaces it (src/demo/runtime/xhr.ts), XMLHttpRequest may only fetch static files.
  const realOpen = RealXhr.prototype.open;
  const realSend = RealXhr.prototype.send;
  const opened = new WeakMap<XMLHttpRequest, string>();
  const guardOpen = function open(this: XMLHttpRequest, method: string, url: string | URL, ...rest: unknown[]) {
    opened.set(this, isStaticRequest(String(url), method) ? '' : String(url));
    return (realOpen as (...a: unknown[]) => void).call(this, method, url, ...rest);
  } as typeof RealXhr.prototype.open;
  const guardSend = function send(this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
    if (opened.get(this)) throw new DOMException('Not sent on a demo page.', 'NetworkError');
    return realSend.call(this, body);
  };
  put(() => {
    RealXhr.prototype.open = guardOpen;
  });
  put(() => {
    RealXhr.prototype.send = guardSend;
  });
  const hadBeacon = typeof navigator.sendBeacon === 'function';
  const beacon = () => true;
  if (hadBeacon) put(() => Object.defineProperty(navigator, 'sendBeacon', { configurable: true, writable: true, value: beacon }));
  // Stripe.js injects its script the moment a module imports it, unless `window.Stripe` already exists: here it does,
  // and makes the runtime's stand-in (no card form, no charge) once the runtime is in place.
  const stripeFactory = (...args: unknown[]) => (state && state.stripe ? state.stripe(...args) : null);
  put(() => Object.defineProperty(w, 'Stripe', { configurable: true, writable: true, value: stripeFactory }));

  // The visitor: the demo's Owner, where the Auth SDK reads its stored user.
  const config = demoFirebaseConfig(industry);
  const uid = demoOwnerUid(industry);
  const authKey = `firebase:authUser:${config.apiKey}:[DEFAULT]`;
  const now = Date.now();
  stores.local.setItem(
    authKey,
    JSON.stringify({
      uid,
      email: `${uid}@demo.example`,
      emailVerified: true,
      isAnonymous: false,
      providerData: [{ providerId: 'password', uid: `${uid}@demo.example`, displayName: null, email: `${uid}@demo.example`, phoneNumber: null, photoURL: null }],
      stsTokenManager: { refreshToken: `demo-refresh-${industry}`, accessToken: demoIdToken(industry, now), expirationTime: now + 3_600_000 },
      createdAt: String(now - 90 * 86_400_000),
      lastLoginAt: String(now),
      apiKey: config.apiKey,
      appName: '[DEFAULT]',
    }),
  );
  stores.local.setItem('prest0_has_signed_in', '1');
  put(() => document.documentElement.setAttribute('data-demo', industry));

  // The self-check: every override, read back off the page itself.
  const own = (target: unknown, name: string) => (target as Record<string, unknown>)[name];
  const checks: Array<[string, () => boolean]> = [
    ['csp', () => document.head.firstChild === csp],
    ['localStorage', () => w.localStorage === stores.local],
    ['sessionStorage', () => w.sessionStorage === stores.session],
    ['indexedDB', () => !idb || Object.keys(idbOwn).every((name) => own(w.indexedDB, name) === idbOwn[name])],
    [
      'BroadcastChannel',
      () => {
        if (!RealChannel) return true;
        const probe = new w.BroadcastChannel('x');
        try {
          return probe.name.includes(`${prefix}${tab}:`);
        } finally {
          probe.close();
        }
      },
    ],
    ['fetch', () => w.fetch === demoFetch],
    ['XMLHttpRequest', () => w.XMLHttpRequest === RealXhr && RealXhr.prototype.open === guardOpen && RealXhr.prototype.send === guardSend],
    ['sendBeacon', () => !hadBeacon || navigator.sendBeacon === beacon],
    ['Stripe', () => own(w, 'Stripe') === stripeFactory],
  ];
  const broken = checks
    .filter(([, took]) => {
      try {
        return !took();
      } catch {
        return true;
      }
    })
    .map(([name]) => name);

  state = {
    industry,
    realSession: hasRealSession,
    signedInBefore,
    natives,
    stores,
    broken,
    release(next: DemoRoute) {
      // A broken page never hands its requests on: what it holds is never answered or sent.
      if (broken.length) return;
      route = next;
      held.splice(0).forEach((go) => go());
    },
    routed: () => route !== null,
    stripe: null,
    forget() {
      keep = false;
      stores.local.clear();
      stores.session.clear();
      try {
        natives.sessionStorage.removeItem(mirrorKey);
      } catch {
        /* nothing kept */
      }
    },
  };
  if (broken.length) {
    drawFailure();
    throw new Error(`The demo page could not be isolated (${broken.join(', ')}): the app does not start.`);
  }
  return state;
}
